import Checkbox from "@app/components/checkbox";
import { ConsentActionType, PathnameUrl } from "@app/constants";
import ConsentLogService from "@app/services/http/consent.log.service";
import { CreateConsentLogPayload } from "@app/types";
import useObservable from "@core/hooks/use-observable.hook";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Environment } from "@environments/environment";
import AccountService from "@app/services/http/account.service";
import { openPortalDialog } from "@app/services/modal.service";
import ConfirmModal from "@app/components/confirm-modal";

export default function EkycConsent() {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isRejected, setIsRejected] = useState<boolean>(false);
  const [isEnableBtnScan, setIsEnableBtnScan] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef<ReCAPTCHA>(null);

  const onChangeCaptcha = async (token: string) => {
    setCaptchaToken(token);
  };

  const navigate = useNavigate();

  const { subscribeUntilDestroy } = useObservable();

  // Lấy thông tin User hiện tại từ hệ thống
  useEffect(() => {
    subscribeUntilDestroy(AccountService.getMyInfo(), (res) => {
      if (res) {
        if (res.isVerify === 1) {
          openPortalDialog(ConfirmModal, {
            message:
              "Bạn đã xác minh danh tính. Không cần thực hiện lại thao tác này!",
          });
          navigate("/");
        }
      }
    });
  }, []);

  // Hàm hỗ trợ gọi API lưu Consent Log ngầm (không chặn UI)
  const logConsent = (
    action: ConsentActionType,
    actionDescription: string,
    reCapChaToken?: string
  ) => {
    const body: CreateConsentLogPayload = {
      action: action,
      actionDescription: actionDescription,
      captChaToken: reCapChaToken,
    };

    subscribeUntilDestroy(ConsentLogService.createConsentLog(body), (res) => {
      if (res.action === ConsentActionType.CHECKED_CONSENT) {
        setIsEnableBtnScan(true);
      } else if (res.action === ConsentActionType.ACCEPTED_AND_SCAN) {
        captchaRef.current?.reset();
        setCaptchaToken("");
        navigate(PathnameUrl.EKYC);
      }
    });
  };

  // 1. Xử lý khi tick / untick checkbox
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Chặn mọi hành vi lan truyền event gây gián đoạn UI
    e.stopPropagation();

    const checked = e.target.checked;

    setIsChecked(checked);

    // Chỉ lưu log khi người dùng chuyển sang trạng thái ĐỒNG Ý
    if (checked) {
      logConsent(
        ConsentActionType.CHECKED_CONSENT,
        "Đã đọc và tích chọn Đồng ý cung cấp dữ liệu eKYC khuôn mặt."
      );
    }
  };

  // 2. Xử lý khi bấm nút [ ĐỒNG Ý VÀ QUÉT CAMERA ]
  const handleAcceptAndScan = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();

    if (!captchaToken) {
      openPortalDialog(ConfirmModal, {
        message: "Vui lòng xác thực reCAPTCHA!",
      });

      return;
    }

    if (!isChecked) return;

    // Ghi log hành vi bấm nút
    logConsent(
      ConsentActionType.ACCEPTED_AND_SCAN,
      "Bấm nút Đồng ý và mở camera quét eKYC.",
      captchaToken
    );
  };

  // 3. Xử lý khi bấm nút [ TÔI TỪ CHỐI ]
  const handleReject = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Ghi log hành vi từ chối
    logConsent(
      ConsentActionType.REJECTED_EKYC,
      "Đã từ chối xác thực qua camera eKYC."
    );

    setIsRejected(true);
  };

  // 4. Khi bấm nút [ QUAY LẠI TRANG CHỦ ]
  const handleBackToHome = () => {
    navigate(PathnameUrl.GPU_HUB);
  };

  // =========================================================================
  // GIAO DIỆN KHÁCH HÀNG TỪ CHỐI (REJECTED SCREEN)
  // =========================================================================
  if (isRejected) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 max-w-2xl w-full p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-red-600 mb-4 flex items-center gap-2">
            🖥️ BẠN ĐÃ TỪ CHỐI XÁC THỰC CAMERA (eKYC)
          </h2>

          <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-4">
            Bạn đã từ chối sử dụng camera để thực hiện xác minh danh tính
            (eKYC). Do quá trình xác minh chưa hoàn tất, tài khoản hiện{" "}
            <b>
              chưa đáp ứng điều kiện sử dụng các dịch vụ yêu cầu xác minh danh
              tính của PCRENDER.
            </b>
            <br />
            <br />
            Để đảm bảo an toàn và bảo vệ thông tin cá nhân,{" "}
            <b>
              PCRENDER chỉ thực hiện xác minh danh tính (eKYC) thông qua hệ
              thống xác minh trên website.
            </b>
            <br />
            <br />
            <b>Lưu ý:</b> Vui lòng{" "}
            <b>
              không gửi hình ảnh Căn cước/CCCD, hình ảnh khuôn mặt hoặc giấy tờ
              tùy thân
            </b>{" "}
            qua Fanpage, Discord hoặc các kênh hỗ trợ khác. PCRENDER không thực
            hiện xác minh danh tính thủ công qua các kênh này.
            <br />
            <br />
            Bạn có thể thực hiện lại eKYC trên website bất kỳ lúc nào khi có nhu
            cầu sử dụng dịch vụ yêu cầu xác minh danh tính.
          </p>

          {/* <p className="text-slate-700 text-sm md:text-base font-medium mb-2">
            Vui lòng liên hệ với đội ngũ hỗ trợ của PCRENDER để được xử lý định
            danh thủ công:
          </p> */}

          {/* <ul className="list-disc list-inside text-slate-600 text-sm md:text-base space-y-1 mb-6 bg-slate-50 p-4 rounded-lg border border-slate-100">
            <li>Nhắn tin qua Fanpage</li>
            <li>
              Hotline:{" "}
              <strong className="text-slate-900 font-semibold">
                083 321 8888
              </strong>
            </li>
          </ul> */}

          {/* <p className="text-slate-600 text-sm mb-6">
            Đội ngũ tư vấn viên của chúng tôi sẽ hỗ trợ bạn.
          </p> */}

          <button
            onClick={handleBackToHome}
            className="w-full bg-slate-800 hover:bg-slate-900 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 shadow-sm"
          >
            QUAY LẠI TRANG CHỦ
          </button>
        </div>
      </div>
    );
  }

  // =========================================================================
  // GIAO DIỆN CHÍNH (CONSENT FORM)
  // =========================================================================
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 max-w-2xl w-full p-6 md:p-8">
        {/* Header */}
        <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          🔒 XÁC THỰC TÀI KHOẢN NGƯỜI DÙNG
        </h2>

        {/* Thông tin mô tả */}
        <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-4">
          Nhằm mục đích ngăn chặn các hành vi giả mạo, gian lận thanh toán, lạm
          dụng máy chủ (tấn công DDoS…), đồng thời tuân thủ quy định định danh
          người dùng của Luật An ninh mạng,{" "}
          <strong className="text-slate-800">pcrender.com</strong> cần tiến hành
          xác thực tài khoản của bạn.
        </p>

        {/* Box Chi tiết & Cam kết */}
        <div className="bg-blue-50/70 border-l-4 border-blue-600 p-4 rounded-r-lg text-slate-700 text-xs md:text-sm space-y-3 mb-6">
          <p>
            <strong className="text-slate-900">Hình thức xác thực:</strong> Quét
            camera nhận diện khuôn mặt sinh trắc học (eKYC) và đối chiếu thông
            tin Giấy tờ tùy thân (CCCD/Hộ chiếu).
          </p>
          <div>
            <strong className="text-slate-900 block mb-1">
              Cam kết bảo mật dữ liệu:
            </strong>
            <ul className="list-disc list-inside space-y-1 pl-1 text-slate-600">
              <li>
                Toàn bộ hình ảnh khuôn mặt và giấy tờ của bạn được mã hóa an
                toàn, không chia sẻ cho bất kỳ bên thứ ba nào vì mục đích thương
                mại.
              </li>
            </ul>
          </div>
        </div>

        <hr className="border-slate-200 my-6" />

        {/* Checkbox Đồng ý */}
        <div className="flex items-start gap-3 mb-6">
          <Checkbox
            id="consent-checkbox"
            checked={isChecked}
            onChange={handleCheckboxChange}
            className="mt-1 h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer accent-blue-600"
          />
          <label
            htmlFor="consent-checkbox"
            className="text-sm md:text-base text-slate-800 leading-snug cursor-pointer select-none"
          >
            Tôi đồng ý cho phép{" "}
            <strong className="font-semibold">pcrender.com</strong> xử lý dữ
            liệu hình ảnh và sinh trắc học khuôn mặt của tôi cho mục đích xác
            thực tài khoản nêu trên.
          </label>
        </div>

        {/* Widget reCAPTCHA */}
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={Environment.RE_CAPCHA_SITE_KEY}
          onChange={onChangeCaptcha}
        />

        {/* Nút Đồng ý & Quét Camera */}
        <div className="mb-4">
          <button
            onClick={handleAcceptAndScan}
            disabled={!isChecked || !isEnableBtnScan || !captchaToken}
            className={`w-full font-bold py-3 px-4 rounded-lg transition-all duration-200 shadow-md ${
              isChecked && isEnableBtnScan && captchaToken
                ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer active:scale-[0.99]"
                : "bg-blue-300 text-white/80 cursor-not-allowed"
            }`}
          >
            ĐỒNG Ý VÀ QUÉT CAMERA
          </button>
          <span className="block text-xs text-slate-500 mt-1 text-center md:text-left">
            Nhấp vào đây để bắt đầu mở camera thực hiện xác thực nhanh trong 1
            phút.
          </span>
        </div>

        {/* Nút Từ chối */}
        <div>
          <button
            onClick={handleReject}
            className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
          >
            TÔI TỪ CHỐI / XÁC THỰC BẰNG CÁCH KHÁC
          </button>
          <span className="block text-xs text-slate-500 mt-1 text-center md:text-left">
            Nhấp vào đây nếu bạn không muốn chia sẻ dữ liệu khuôn mặt. Bạn sẽ
            được chuyển hướng sang phương thức xác thực thay thế.
          </span>
        </div>
      </div>
    </div>
  );
}
